import { createApp } from 'vue';
import { createPinia } from 'pinia';

import App from './App.vue';
import router from './router';
import '@/index.css';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import { definePreset } from '@primeuix/themes';
import Nora from '@primeuix/themes/nora';
import * as Sentry from '@sentry/vue';
import Tooltip from 'primevue/tooltip';
import { APP_VERSION } from '@/constants/version';

const app = createApp(App);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: `tahani-vue@${APP_VERSION}`,
  tracesSampleRate: 0.2,
});
Sentry.setTag('client_version', APP_VERSION);

app.use(createPinia());
app.use(router);

// Sage mint primary (state, selection, progress) on a cream surface. Filled primary
// buttons use ocean instead: the main action is the only blue on a screen.
// Keep these hex values in sync with the @theme scales in src/theme.css.
const NoraCustom = definePreset(Nora, {
  primitive: {
    borderRadius: {
      none: '0px',
      xs: '4px',
      sm: '6px',
      md: '8px',
      lg: '12px',
      xl: '16px',
    },
  },
  semantic: {
    primary: {
      50: '#ecfbf2',
      100: '#d2f5e1',
      200: '#9fe7c0',
      300: '#6bdba4',
      400: '#3ece90',
      500: '#12b478',
      600: '#09764d',
      700: '#105f3f',
      800: '#0f4c32',
      900: '#0d3926',
      950: '#032113',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#fff9ec',
          100: '#fbf4e3',
          200: '#eadfc4',
          300: '#dccfaf',
          400: '#a5a396',
          500: '#6b6f75',
          600: '#4f555c',
          700: '#3a3f45',
          800: '#2a2e33',
          900: '#1a1d21',
          950: '#111316',
        },
        formField: {
          borderColor: '{surface.200}',
          hoverBorderColor: '{surface.300}',
          focusBorderColor: 'transparent',
        },
      },
    },
    formField: {
      focusRing: {
        width: '2px',
        style: 'solid',
        color: '{primary.600}',
        offset: '0',
        shadow: 'none',
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '#17688d',
              hoverBackground: '#065a7c',
              activeBackground: '#094864',
              borderColor: '#17688d',
              hoverBorderColor: '#065a7c',
              activeBorderColor: '#094864',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: { color: '#2d7da5', shadow: 'none' },
            },
          },
        },
      },
    },
    progressbar: {
      value: { background: '{primary.400}' },
      // Nora's default track (surface.300) leaves too little contrast with sage.
      colorScheme: { light: { root: { background: '{surface.100}' } } },
    },
  },
});

app.use(PrimeVue, {
  theme: {
    preset: NoraCustom,
    options: {
      darkModeSelector: '.dark',
    },
  },
});
app.directive('tooltip', Tooltip);
app.use(ToastService);
app.use(ConfirmationService);

app.mount('#app');
